import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { IBugsSubmit } from './bugs.types';
import TextButton from '../controls/buttons/TextButton';
import Alert from '@components/elements/Alert';
import { menuItems } from '@app_config/sideMenu';
import { APP_CONFIG } from '@app_config/app';
import {
  FlexRow,
  FlexRowSpaced,
  FlexItem,
  FlexSpacer,
} from '../elements/flexStyles';

import { TextTitle, TextSmall } from '@components/elements/TextStyles';
import InputField from '@as_core/components/inputs/InputField';
import SelectField, {
  ISelectOption,
} from '@as_core/components/select/SelectField';
import useBugReports from '@as_core/bugsReporting/useBugReports';

type LocalErrorsT = {
  title: string;
  description: string;
  category: string;
};

const getCategories = () => {

  return [
    ...menuItems.map((m) => ({
      label: m?.name,
      value: m?.id,
    })),
    {
      label: 'Other',
      value: 'other',
    },
    {
      label: 'Suggestion',
      value: 'suggestion',
    },
  ];
};

const BugsSubmit = ({
  setIsNewBugReported,
  inEditBug,
  setInEditBug,
}: IBugsSubmit) => {
  const debug = false;
  const { updateBugReport, createBugReport } = useBugReports();
  const [bugCategories] = useState<ISelectOption[]>(getCategories());
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [values, setValues] = useState({
    title: '',
    description: '',
    category: '',
  });
  const [errors, setErrors] = useState<LocalErrorsT>({
    title: null,
    description: null,
    category: null,
  });

  const handleAlert = () => {
    setIsNewBugReported(true);
    setAlertOpen(false);
  };

  const submitBugReport = async () => {
    if (debug) console.log('SUBMIT: title, description', values);

    const localErrors: LocalErrorsT = {
      title: null,
      description: null,
      category: null,
    };

    if (values.title === '') {
      localErrors.title = 'Title required*';
    }

    if (values.description === '') {
      localErrors.description = 'Description required*';
    }

    if (values.category === '') {
      localErrors.category = 'Category required*';
    }

    setErrors(localErrors);

    if (!Object.values(localErrors).find((error) => error !== null)) {
      const payload = {
        system: APP_CONFIG.name,
        title: values.title,
        description: values.description,
        category: values.category,
      };

      if (inEditBug) {
        await updateBugReport(inEditBug.uuid, payload);
        setInEditBug(null);
      } else {
        await createBugReport(payload);
        setIsNewBugReported(true);
      }
      setAlertMessage(
        `Bug Report Successfully ${
          inEditBug ? 'Updated' : 'Submitted'
        }: ${new Date().toLocaleString()}`
      );
      setAlertOpen(true);
      setValues({
        title: '',
        description: '',
        category: '',
      });
    }
  };
  if (debug) console.log('title, description', values);

  useEffect(() => {
    if (inEditBug) {
      setValues({
        title: inEditBug?.title,
        description: inEditBug?.description,
        category: inEditBug?.category,
      });
      setErrors({
        title: null,
        description: null,
        category: null,
      });
    }
  }, [inEditBug]);

  const onCancelEdit = () => {
    setValues({
      title: '',
      description: '',
      category: '',
    });
    setInEditBug(null);
  };

  const onChange = (value: unknown, key: string) => {
    if (errors[key]) {
      setErrors((previous) => ({ ...previous, [key]: null }));
    }
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <>
      <Content>
        <FlexRowSpaced width={'100%'} >
          <FlexItem>
            <Heading color={'accent'}>
              {inEditBug
                ? 'Update a previously submitted bug'
                : 'Submit a newly discovered bug or suggestion'}
            </Heading>
          </FlexItem>
          <FlexItem>
            {inEditBug ? (
              <>
                <TextButton width={100} height={32} label='Cancel' onClick={onCancelEdit} />
                <FlexSpacer width={10} />
              </>
            ) : null}
            <TextButton width={100} height={32} label={inEditBug ? 'Update' : 'Submit'} onClick={submitBugReport} />
          </FlexItem>
        </FlexRowSpaced>
        <FlexRow>
          <FlexItem>
            <TextSmall>
              {inEditBug
                ? 'Edit either description, category or title'
                : 'Enter a short name and detailed description below.'}
            </TextSmall>
          </FlexItem>
        </FlexRow>
        <FlexRow width={'100%'}>
          <FlexItem width={'50%'}>
            <InputField
              id={'title'}
              type={'text'}
              placeholder={'Enter short title'}
              label={errors.title || 'Title'}
              value={values.title}
              onChange={(e) => onChange(e.target.value, 'title')}
              error={Boolean(errors.title)}
            />
          </FlexItem>
          <FlexItem width={'50%'}>
            <SelectField
              labelId='category'
              id='category'
              value={values.category}
              label={errors.category || 'Category'}
              onChange={(e) => onChange(e.target.value, 'category')}
              error={Boolean(errors.category)}
              options={bugCategories}
              fullWidth
            />
          </FlexItem>
        </FlexRow>
        <FlexRow width={'100%'}>
          <FlexItem width={'100%'}>
            <InputField
              id='description'
              label={errors.description || 'Description'}
              value={values.description}
              multiline
              rows={3}
              onChange={(e) => onChange(e.target.value, 'description')}
              error={Boolean(errors.description)}
            />
          </FlexItem>
        </FlexRow>
      </Content>
      <Alert
        type={'general'}
        title={'Bug Report Submitted'}
        message={alertMessage}
        alertOpen={alertOpen}
        closeAlert={handleAlert}
      />
    </>
  );
};

export default BugsSubmit;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 680px;
`;

const Heading = styled(TextTitle)`
  font-size: ${(p) => p.theme.sizes.large};
`;
